import React, { useState, useRef, useEffect } from 'react';
import { AppSelect } from '../select';

const FilterDropdown = ({
  filterData,
  onFilterChange,
  onReset,
  onApply,
  onOpen,
  filterFields = [],
  options = {},
  uniqueId = 'default',
  showDateFilter = true,
  dateLabel = 'Date',
  offcanvasMode = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening && onOpen) {
      onOpen();
    }
  };

  const handleReset = (e) => {
    e.stopPropagation();
    if (onReset) onReset();
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (onApply) onApply();
    setIsOpen(false);
  };

  // Default date filter
  const renderDateFilter = () => (
    <div className="mb-3">
      <label className="form-label">{dateLabel}</label>
      <div className="row g-2">
        <div className="col">
          <label htmlFor={`dateFrom-${uniqueId}`}>From</label>
          <input
            type="date"
            className="form-control"
            id={`dateFrom-${uniqueId}`}
            name="dateFrom"
            placeholder="From"
            value={filterData.dateFrom}
            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="col">
          <label htmlFor={`dateTo-${uniqueId}`}>To</label>
          <input
            type="date"
            className="form-control"
            id={`dateTo-${uniqueId}`}
            name="dateTo"
            placeholder="To"
            value={filterData.dateTo}
            onChange={(e) => onFilterChange('dateTo', e.target.value)}
            autoComplete="off"
          />
        </div>
      </div>
    </div>
  );

  // Render a date range filter field
  const renderDateRangeFilter = (field) => {
    const fromField = field.fromField || `${field.name}From`;
    const toField = field.toField || `${field.name}To`;
    
    return (
      <div className="mb-3" key={field.name}>
        <label className="form-label">{field.label}</label>
        <div className="row g-2">
          <div className="col">
            <label htmlFor={`${fromField}-${uniqueId}`} className="small">From</label>
            <input
              type="date"
              className="form-control"
              id={`${fromField}-${uniqueId}`}
              name={fromField}
              value={filterData[fromField] || ''}
              onChange={(e) => onFilterChange(fromField, e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="col">
            <label htmlFor={`${toField}-${uniqueId}`} className="small">To</label>
            <input
              type="date"
              className="form-control"
              id={`${toField}-${uniqueId}`}
              name={toField}
              value={filterData[toField] || ''}
              onChange={(e) => onFilterChange(toField, e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>
      </div>
    );
  };

  // Render a select filter field
  const renderSelectFilter = (field) => {
    const fieldOptions = field.options || options[`${field.name}Options`] || [];
    
    const isHardcodedField = ['approvalStatus', 'proposalStatus', 'projectStatus', 'status'].includes(field.name);
    const shouldRender = fieldOptions.length > 0 || isHardcodedField;
    
    if (!shouldRender) {
      return (
        <div className="mb-3" key={field.name}>
          <label className="form-label">{field.label}</label>
          <select className="form-select" disabled>
            <option>No options available</option>
          </select>
        </div>
      );
    }
    
    return (
      <div className="mb-3" key={field.name}>
        <label className="form-label">{field.label}</label>
        <AppSelect
          name={field.name}
          value={filterData[field.name]}
          onChange={(n, val) => onFilterChange(field.name, val)}
          options={fieldOptions}
          placeholder={`Select ${field.label.toLowerCase()}`}
          required={false}
          valueField={field.valueField || "value"}
          labelField={field.labelField || "label"}
          searchField={field.searchField || "label"}
          create={false}
          multiple={field.multiple || false}
          dropdownParent={null}
        />
      </div>
    );
  };

  const renderTextFilter = (field) => {
    return (
      <div className="mb-3" key={field.name}>
        <label className="form-label">{field.label}</label>
        <input
          type="text"
          className="form-control"
          name={field.name}
          value={filterData[field.name] || ''}
          onChange={(e) => onFilterChange(field.name, e.target.value)}
          placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
          autoComplete="off"
        />
      </div>
    );
  };

  if (offcanvasMode) {
    return (
      <>
        {showDateFilter && renderDateFilter()}
        {filterFields.map(field => {
          if (field.type === 'dateRange') return renderDateRangeFilter(field);
          if (field.type === 'text' || field.type === 'input') return renderTextFilter(field);
          return renderSelectFilter(field);
        })}
        <div className="d-flex gap-2 mt-4">
          <button type="button" className="btn btn-outline-primary flex-fill" onClick={(e) => { e.stopPropagation(); if (onReset) onReset(); }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary flex-fill" onClick={(e) => { e.stopPropagation(); if (onApply) onApply(); }}>
            Apply
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="col-6 col-sm-4 col-md-2 col-xl">
      <div className={`dropdown${isOpen ? ' show' : ''}`} ref={dropdownRef}>
        <a
          href="#"
          className="btn"
          id={`filter-dropdown-button-${uniqueId}`}
          aria-label="Open filter options"
          aria-expanded={isOpen}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            className="icon icon-tabler icons-tabler-outline icon-tabler-filter" 
            aria-hidden="true"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 4h16v2.172a2 2 0 0 1 -.586 1.414l-4.414 4.414v7l-6 2v-8.5l-4.48 -4.928a2 2 0 0 1 -.52 -1.345v-2.227z" />
          </svg>
          Filter
        </a>

        {/* Dropdown menu — React-controlled, no Bootstrap JS */}
        {isOpen && (
          <div
            className="dropdown-menu p-0 show"
            style={{ minWidth: '400px' }}
            onClick={(e) => e.stopPropagation()}
            role="menu"
            aria-labelledby={`filter-dropdown-button-${uniqueId}`}
          >
            {/* Inner wrapper for scroll + sticky footer */}
            <div className="d-flex flex-column" style={{ maxHeight: '400px' }}>

              {/* Scrollable filter content */}
              <div style={{ overflowY: 'auto', flex: '1 1 auto', padding: '1rem' }}>
                {showDateFilter && renderDateFilter()}
                {filterFields.map(field => {
                  if (field.type === 'dateRange') {
                    return renderDateRangeFilter(field);
                  }
                  if (field.type === 'text' || field.type === 'input') {
                    return renderTextFilter(field);
                  }
                  return renderSelectFilter(field);
                })}
              </div>

              <div className="dropdown-divider m-0"></div>

              {/* Sticky footer */}
              <div
                className="d-flex justify-content-end gap-2 p-2"
                style={{ position: 'sticky', bottom: 0, background: 'white' }}
              >
                <button type="button" className="btn btn-link link-secondary" onClick={handleReset}>
                  Reset
                </button>
                <button type="button" className="btn btn-primary" onClick={handleApply}>
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
